import React, { useEffect, useState } from 'react';
import { Card, Button, Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';
import { FaSearch, FaEraser } from 'react-icons/fa';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { lookupGEsPorGrupoGe } from '~/Services/GestaoIPTUService';
import styles from '../styles.module.css';

const labelStyle = { fontSize: 12, fontWeight: 600, marginBottom: 2 };

const TIPOS_MOVIMENTO = [
  { codigo: 1, label: 'Estoque -> Vendido', chipClass: styles.movChip1 },
  { codigo: 2, label: 'Vendido -> Estoque', chipClass: styles.movChip2 },
  { codigo: 3, label: 'Estoque -> Estoque', chipClass: styles.movChip3 },
  { codigo: 4, label: 'Vendido -> Vendido', chipClass: styles.movChip4 },
  { codigo: 5, label: 'Sem Movimento',      chipClass: styles.movChip5 },
];

const FiltroGestaoIPTU = ({
  filtros,
  onChange,
  onBuscar,
  onLimpar,
  familias,
  gruposGe,
  gruposEmpresa,
  cidades,
  carneStatus,
  statusImovel,
  statusDebitoIPTU,
  opcoesIPTU,
  habilitaCondominio,
  carregando,
}) => {
  const set = (campo, valor) => onChange({ ...filtros, [campo]: valor });

  const toOptions = (list, labelFn) =>
    (list || []).map((item) => ({ value: item.codigo, label: labelFn(item) }));

  // ListGE — réplica de UImovelCarne.pas:LGrupoGEChange (2003-2017). Quando o
  // usuário escolhe um Grupo GE, busca as Codigo_Grupo_Empresa permitidas a ele
  // nesse grupo e usa pra filtrar Empreendimento (AbreTabFamilia:1101-1102).
  // null = nenhum filtro de GE aplicado (Grupo GE e GE ambos vazios).
  // Melhoria filtros W-0718 — também respeita o novo filtro GE direto:
  // quando GE estiver setado, gesPermitidas = [filtros.ge] (singleton).
  const [gesPermitidas, setGesPermitidas] = useState(null);
  useEffect(() => {
    if (filtros.ge) {
      setGesPermitidas([filtros.ge]);
      return undefined;
    }
    if (!filtros.grupoGe) {
      setGesPermitidas(null);
      return undefined;
    }
    let ativo = true;
    lookupGEsPorGrupoGe(filtros.grupoGe)
      .then((lista) => { if (ativo) setGesPermitidas(Array.isArray(lista) ? lista : []); })
      .catch(() => { if (ativo) setGesPermitidas([]); });
    return () => { ativo = false; };
  }, [filtros.grupoGe, filtros.ge]);

  // Auto-clear Empreendimento(s) que não estão mais entre as GEs permitidas.
  // Empreendimento agora é array (Melhoria filtros W-0718 — multi-select).
  // Roda só quando `gesPermitidas` é atualizado — evita loop (não inclui `filtros` nas deps).
  useEffect(() => {
    if (!gesPermitidas) return;
    const atuais = filtros.codigoFamilia || [];
    if (atuais.length === 0) return;
    const filtradas = atuais.filter((cod) => {
      const fam = (familias || []).find((f) => f.codigo === cod);
      return !fam || gesPermitidas.indexOf(fam.codigo_grupo_empresa) !== -1;
    });
    if (filtradas.length !== atuais.length) {
      onChange({ ...filtros, codigoFamilia: filtradas });
    }
  }, [gesPermitidas]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restringe Empreendimento por Cidade + Grupo GE/GE (cumulativo). Réplica de
  // UImovelCarne.pas:AbreTabFamilia (1094-1117) — Codigo_Cidade e/ou
  // Codigo_Grupo_Empresa IN (ListGE).
  const familiasFiltradas = (familias || []).filter((f) => {
    if (filtros.codigoCidade && f.codigo_cidade !== filtros.codigoCidade) return false;
    if (gesPermitidas && gesPermitidas.indexOf(f.codigo_grupo_empresa) === -1) return false;
    return true;
  });
  const opcoesFamilias       = toOptions(familiasFiltradas, (f) => f.descricao);
  const opcoesGruposGe       = toOptions(gruposGe,      (g) => `${g.codigo} - ${g.descricao}`);
  const opcoesGruposEmpresa  = toOptions(gruposEmpresa, (g) => `${g.codigo} - ${g.descricao}`);
  // Filtra cidades com Descricao_Cidade NULL/vazia que escapem do BE — evita
  // opção em branco no dropdown (faixa azul sem texto).
  const cidadesValidas       = (cidades || []).filter(
    (c) => c && c.descricao && String(c.descricao).trim() !== ''
  );
  const opcoesCidades        = toOptions(cidadesValidas, (c) => c.descricao);
  const opcoesCarneStatus    = toOptions(carneStatus, (s) => s.descricao);
  const opcoesStatusImovel   = toOptions(statusImovel, (s) => s.descricao);
  const opcoesStatusDebito   = toOptions(statusDebitoIPTU, (s) => s.descricao);

  // Filtros Opção Pagto / Opção Cond — replica lkpFiltroPgto/lkpFiltroCond do Delphi
  // (UImovelCarne.pas:420-453): "-1=Todos" + "0=Vazio" + IPTU_Opcoes WHERE Codigo NOT IN (6,7).
  const opcoesFiltroIPTU = [
    { value: -1, label: 'Todos' },
    { value: 0,  label: 'Vazio' },
    ...(opcoesIPTU || [])
      .filter((o) => o.codigo !== 6 && o.codigo !== 7)
      .map((o) => ({ value: o.codigo, label: o.descricao })),
  ];

  // 4 valores fixos do lkpFiltroOrdemPagto (UImovelCarne.pas:488-497).
  const opcoesOrdemPagamento = [
    { value: -1, label: 'Todos' },
    { value: 0,  label: 'Em Branco' },
    { value: 6,  label: 'Pagar' },
    { value: 7,  label: 'Não Pagar' },
  ];

  // Mesmo componente do ModalDebitosIPTU (<Form.Control type="date">) — permite
  // digitar a data direto, sem depender do datepicker do Material-UI.
  const renderRangeData = (campoIni, campoFim) => (
    <div className="d-flex align-items-center" style={{ gap: 6 }}>
      <span style={{ fontSize: 12 }}>de</span>
      <Form.Control
        type="date"
        value={filtros[campoIni] ? moment(filtros[campoIni]).format('YYYY-MM-DD') : ''}
        onChange={(e) => set(campoIni, e.target.value || null)}
        style={{ width: 140 }}
      />
      <span style={{ fontSize: 12 }}>até</span>
      <Form.Control
        type="date"
        value={filtros[campoFim] ? moment(filtros[campoFim]).format('YYYY-MM-DD') : ''}
        onChange={(e) => set(campoFim, e.target.value || null)}
        style={{ width: 140 }}
      />
    </div>
  );

  const renderMultiSelect = (label, campo, opcoes, obrigatorio) => {
    const valoresAtuais = filtros[campo] || [];
    const todosSel = opcoes.length > 0 && valoresAtuais.length === opcoes.length;
    return (
      <>
        <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 2 }}>
          <Form.Label style={{ ...labelStyle, marginBottom: 0 }}>
            {label}
            {obrigatorio ? <span className="text-danger"> *</span> : null}
          </Form.Label>
          <Button
            variant="link"
            style={{ fontSize: 11, padding: 0, lineHeight: 1 }}
            onClick={() => set(campo, todosSel ? [] : opcoes.map((o) => o.value))}
          >
            {todosSel ? 'Desmarcar todos' : 'Selecionar todos'}
          </Button>
        </div>
        <Select
          isMulti
          options={opcoes}
          value={opcoes.filter((o) => valoresAtuais.includes(o.value))}
          onChange={(opts) => set(campo, (opts || []).map((o) => o.value))}
          placeholder="Todos"
          styles={customStyles}
          components={{ DropdownIndicator }}
          closeMenuOnSelect={false}
        />
      </>
    );
  };

  return (
    <Card className="shadow-sm mb-3">
      <Card.Body>
        {/* Linha 1 — Grupo GE / GE / Data Venda */}
        {/* Grupo GE e GE são mutuamente exclusivos (Melhoria filtros W-0718):
            selecionar um limpa o outro. */}
        <Row className="align-items-end">
          <Col xs={12} sm={6} md={3} lg={3} className="mb-2">
            <Form.Label style={labelStyle}>Grupo GE</Form.Label>
            <Select
              options={opcoesGruposGe}
              value={opcoesGruposGe.find((o) => o.value === filtros.grupoGe) || null}
              onChange={(opt) => onChange({
                ...filtros,
                grupoGe: opt ? opt.value : null,
                ge: opt ? null : filtros.ge,
              })}
              isClearable
              isSearchable
              isDisabled={!!filtros.ge}
              placeholder={filtros.ge ? 'Indisponível (GE selecionado)' : 'Selecione...'}
              styles={customStyles}
              components={{ DropdownIndicator }}
            />
          </Col>
          <Col xs={12} sm={6} md={3} lg={3} className="mb-2">
            <Form.Label style={labelStyle}>GE</Form.Label>
            <Select
              options={opcoesGruposEmpresa}
              value={opcoesGruposEmpresa.find((o) => o.value === filtros.ge) || null}
              onChange={(opt) => onChange({
                ...filtros,
                ge: opt ? opt.value : null,
                grupoGe: opt ? null : filtros.grupoGe,
              })}
              isClearable
              isSearchable
              isDisabled={!!filtros.grupoGe}
              placeholder={filtros.grupoGe ? 'Indisponível (Grupo GE selecionado)' : 'Selecione...'}
              styles={customStyles}
              components={{ DropdownIndicator }}
            />
          </Col>
          <Col xs={12} md={6} lg={6} className="mb-2">
            <Form.Label style={labelStyle}>
              Data Venda<span className="text-danger"> *</span>
            </Form.Label>
            {renderRangeData('dataVendaInicio', 'dataVendaFim')}
          </Col>
        </Row>

        {/* Linha 2 — Cidade / Empreendimento / Cad. Prefeitura */}
        <Row className="align-items-end">
          <Col xs={12} sm={6} md={4} lg={4} className="mb-2">
            <Form.Label style={labelStyle}>Cidade</Form.Label>
            <Select
              options={opcoesCidades}
              value={opcoesCidades.find((o) => o.value === filtros.codigoCidade) || null}
              onChange={(opt) => {
                const novaCidade = opt ? opt.value : null;
                // Empreendimento agora é array (Melhoria filtros W-0718): mantém só as
                // famílias que pertencem à nova cidade.
                const atuais = filtros.codigoFamilia || [];
                const filtradas = novaCidade
                  ? atuais.filter((cod) => {
                      const fam = (familias || []).find((f) => f.codigo === cod);
                      return !fam || fam.codigo_cidade === novaCidade;
                    })
                  : atuais;
                onChange({
                  ...filtros,
                  codigoCidade: novaCidade,
                  codigoFamilia: filtradas,
                });
              }}
              isClearable
              isSearchable
              placeholder={(filtros.grupoGe || filtros.ge) ? 'Todas' : 'Selecione Grupo GE/GE'}
              isDisabled={!filtros.grupoGe && !filtros.ge}
              noOptionsMessage={() => (filtros.grupoGe || filtros.ge)
                ? 'Sem cidades no escopo'
                : 'Selecione um Grupo GE ou GE primeiro'}
              styles={customStyles}
              components={{ DropdownIndicator }}
            />
          </Col>
          <Col xs={12} sm={6} md={5} lg={5} className="mb-2">
            <Form.Label style={labelStyle}>Empreendimento</Form.Label>
            <Select
              isMulti
              options={opcoesFamilias}
              value={opcoesFamilias.filter((o) => (filtros.codigoFamilia || []).includes(o.value))}
              onChange={(opts) => set('codigoFamilia', (opts || []).map((o) => o.value))}
              isSearchable
              closeMenuOnSelect={false}
              placeholder="Selecione um ou mais..."
              styles={customStyles}
              components={{ DropdownIndicator }}
            />
          </Col>
          <Col xs={12} sm={6} md={3} lg={3} className="mb-2">
            <Form.Label style={labelStyle}>Cadastro Prefeitura</Form.Label>
            <Form.Control
              type="text"
              maxLength={30}
              value={filtros.cadPrefeitura || ''}
              onChange={(e) => set('cadPrefeitura', e.target.value)}
            />
          </Col>
        </Row>

        {/* Linha 3 — Quadra / Lote / Código Ativo / Última Rescisão */}
        <Row className="align-items-end">
          <Col xs={6} sm={3} md={2} lg={1} className="mb-2">
            <Form.Label style={labelStyle}>Quadra</Form.Label>
            <Form.Control
              type="text"
              value={filtros.quadra}
              onChange={(e) => set('quadra', e.target.value)}
            />
          </Col>
          <Col xs={6} sm={3} md={2} lg={1} className="mb-2">
            <Form.Label style={labelStyle}>Lote</Form.Label>
            <Form.Control
              type="text"
              value={filtros.lote}
              onChange={(e) => set('lote', e.target.value)}
            />
          </Col>
          <Col xs={12} sm={6} md={3} lg={3} className="mb-2">
            <Form.Label style={labelStyle}>Código Ativo</Form.Label>
            <Form.Control
              type="number"
              value={filtros.codigoAtivo}
              onChange={(e) => set('codigoAtivo', e.target.value)}
            />
          </Col>
          <Col xs={12} md={5} lg={7} className="mb-2">
            <Form.Label style={labelStyle}>Última Rescisão</Form.Label>
            {renderRangeData('ultimaRescisaoInicio', 'ultimaRescisaoFim')}
          </Col>
        </Row>

        {/* Linha 3 — Ordem Pagto / Opção Pagto / Opção Cond / Status Débito IPTU */}
        <Row className="align-items-end">
          <Col xs={12} sm={6} md={3} className="mb-2">
            <Form.Label style={labelStyle}>Ordem Pagamento</Form.Label>
            <Select
              options={opcoesOrdemPagamento}
              value={opcoesOrdemPagamento.find((o) => o.value === (filtros.filtroOrdemPagamento != null ? filtros.filtroOrdemPagamento : -1))}
              onChange={(opt) => set('filtroOrdemPagamento', opt ? opt.value : -1)}
              styles={customStyles}
              components={{ DropdownIndicator }}
            />
          </Col>
          <Col xs={12} sm={6} md={3} className="mb-2">
            <Form.Label style={labelStyle}>Opção Pagamento</Form.Label>
            <Select
              options={opcoesFiltroIPTU}
              value={opcoesFiltroIPTU.find((o) => o.value === (filtros.filtroOpcaoPagamento != null ? filtros.filtroOpcaoPagamento : -1))}
              onChange={(opt) => set('filtroOpcaoPagamento', opt ? opt.value : -1)}
              styles={customStyles}
              components={{ DropdownIndicator }}
            />
          </Col>
          <Col xs={12} sm={6} md={3} className="mb-2">
            <Form.Label
              style={{ ...labelStyle, opacity: habilitaCondominio ? 1 : 0.5 }}
            >
              Opção Condomínio
            </Form.Label>
            <Select
              isDisabled={!habilitaCondominio}
              options={opcoesFiltroIPTU}
              value={opcoesFiltroIPTU.find((o) => o.value === (filtros.filtroOpcaoCondominio != null ? filtros.filtroOpcaoCondominio : -1))}
              onChange={(opt) => set('filtroOpcaoCondominio', opt ? opt.value : -1)}
              placeholder={habilitaCondominio ? 'Selecione...' : 'Apenas Vertical/Horizontal'}
              styles={customStyles}
              components={{ DropdownIndicator }}
            />
          </Col>
          <Col xs={12} sm={6} md={3} className="mb-2">
            <Form.Label style={labelStyle}>Status Débito IPTU</Form.Label>
            <Select
              options={opcoesStatusDebito}
              value={opcoesStatusDebito.find((o) => o.value === filtros.statusDebitoIptu) || null}
              onChange={(opt) => set('statusDebitoIptu', opt ? opt.value : null)}
              isClearable
              placeholder="Todos"
              styles={customStyles}
              components={{ DropdownIndicator }}
            />
          </Col>
        </Row>

        {/* Linha 4 — Status Ativo / Status Pagamento */}
        <Row className="align-items-end">
          <Col xs={12} md={6} className="mb-2">
            {renderMultiSelect('Status Ativo', 'statusImovel', opcoesStatusImovel, false)}
          </Col>
          <Col xs={12} md={6} className="mb-2">
            {renderMultiSelect('Status Pagamento', 'statusCarne', opcoesCarneStatus, false)}
          </Col>
        </Row>

        {/* Linha 5 — Tipo de Movimento (com cores) */}
        <div style={{ marginTop: 8 }}>
          <Form.Label style={labelStyle}>
            Tipo de Movimento<span className="text-danger"> *</span>
          </Form.Label>
          <div className="d-flex flex-wrap" style={{ gap: '8px 16px' }}>
            {TIPOS_MOVIMENTO.map((t) => {
              const id = `tipo-mov-${t.codigo}`;
              const ativo = (filtros.tiposMov || []).includes(t.codigo);
              return (
                <label
                  key={t.codigo}
                  htmlFor={id}
                  className="d-inline-flex align-items-center"
                  style={{ fontSize: 12, marginBottom: 0, cursor: 'pointer' }}
                >
                  <input
                    id={id}
                    type="checkbox"
                    checked={ativo}
                    onChange={() => {
                      const atual = filtros.tiposMov || [];
                      const novo = atual.includes(t.codigo)
                        ? atual.filter((c) => c !== t.codigo)
                        : [...atual, t.codigo];
                      set('tiposMov', novo);
                    }}
                    style={{ marginRight: 6 }}
                  />
                  <span className={`${styles.movChip} ${t.chipClass}`} />
                  {t.label}
                </label>
              );
            })}
          </div>
        </div>

        <div className="d-flex justify-content-end flex-wrap" style={{ gap: 8, marginTop: 12 }}>
          <Button variant="primary" onClick={onBuscar} disabled={carregando}>
            <FaSearch className="mr-1" /> Buscar
          </Button>
          <Button variant="secondary" onClick={onLimpar} disabled={carregando}>
            <FaEraser className="mr-1" /> Limpar
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default FiltroGestaoIPTU;
